import type { Signal } from '@angular/core'
import { Component, inject, input } from '@angular/core'
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop'

import { NgArrayPipesModule } from 'ngx-pipes'
import type { Observable } from 'rxjs'
import { of, switchMap } from 'rxjs'

import { EditorsService } from '../../api/editors-service/editors.service'
import type { ColophonDto, MagazineDto } from '../../api/responses'
import { PersonLinkDirective } from '../../shared/directives/person-link.directive'

type EditorialBoardRole = keyof NonNullable<MagazineDto['editorial_board']>

@Component({
  selector: 'dhp-editorial-board',
  templateUrl: './editorial-board.component.html',
  styleUrl: './editorial-board.component.scss',
  imports: [PersonLinkDirective, NgArrayPipesModule],
})
export class EditorialBoardComponent {
  private readonly editorsService = inject(EditorsService)

  editionId = input.required<number>()
  showLinks = input<boolean>(true)
  magazine = input.required<MagazineDto>()

  public editorialBoard!: Signal<ColophonDto[] | undefined>

  constructor() {
    this.editorialBoard = toSignal(
      toObservable(this.magazine).pipe(
        takeUntilDestroyed(),
        switchMap(mag =>
          mag.selectionAndEssence
            ? this.getEditorialBoardForSelectionAndEssence()
            : this.editorsService.getEditorialBoard(this.editionId())
        )
      )
    )
  }

  /**
   * Selection and Essence has Teams of editors. Each team is responsible for a
   * next batch/edition of S&E articles to be published. All these editors of
   * the teams have been imported to the main magazine's editorial board This
   * method reads the editorial board from the magazine and transforms it into a
   * colophon format which is used for proper magazine editions
   */
  private getEditorialBoardForSelectionAndEssence(): Observable<ColophonDto[]> {
    const editorialBoard = this.magazine().editorial_board

    const colophonBoard = new Array<ColophonDto>()
    for (const { type, title } of this.typeLabelMap) {
      const authors = editorialBoard?.[type]

      if (authors?.length) {
        colophonBoard.push({
          type,
          title,
          authors,
        })
      }
    }

    return of(colophonBoard)
  }

  private readonly typeLabelMap: readonly {
    type: EditorialBoardRole
    title: string
  }[] = [{ type: 'caseLawAuthor', title: 'Redactie' }]
}
